import React from "react";
import { Container, Grid, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import SEO from "../components/SEO";

const AboutContainer = styled(Container)(({ theme }) => ({
  marginTop: "4rem",
  marginBottom: "4rem",
  padding: "2rem 1rem",
  [theme.breakpoints.down("sm")]: {
    textAlign: "center",
  },
}));

const AboutText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  lineHeight: 1.6,
  maxWidth: "100%",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.9rem",
    padding: "0 1rem",
  },
}));

const AboutImage = styled("img")(({ theme }) => ({
  width: "100%",
  maxWidth: "400px",
  borderRadius: "8px",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
  [theme.breakpoints.down("sm")]: {
    maxWidth: "300px",
  },
}));

const ContentCard = styled("div")(({ theme }) => ({
  marginBottom: "3rem",
  "& .value-item": {
    padding: "2rem",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "10px",
    transition: "all 0.3s ease",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    "&:hover": {
      boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
      transform: "translateY(-5px)",
    },
  },
  "& .feature-icon": {
    width: "80px",
    height: "80px",
    margin: "0 auto 1.5rem",
    borderRadius: "50%",
    backgroundColor: "#1976d2", // Blue color matching the image
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontSize: "2rem",
    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
  },
  "& h4": {
    marginBottom: "1rem",
    fontSize: "1.5rem",
    fontWeight: 600,
    width: "100%",
    textAlign: "center",
    color: "#FFC107", // Yellow color for titles
  },
  "& p": {
    color: theme.palette.text.secondary,
    fontSize: "1rem",
    textAlign: "center",
    margin: "0",
  },
}));

const MissionCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(3),
  borderRadius: '10px',
  marginBottom: theme.spacing(4),
  boxShadow: '0 2px 15px rgba(0,0,0,0.08)',
  '& h3': {
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
}));

const Section = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    viewport={{ once: true }}
  >
    {children}
  </motion.div>
);

const AboutUs = () => {
  const facts = [
    {
      icon: "🏢",
      title: "Nature of Business",
      content: "Wholesaler / Distributor"
    },
    {
      icon: "🏪",
      title: "Business Type", 
      content: "Retail & Bulk Supply"
    },
    {
      icon: "📍",
      title: "Location",
      content: "Akola, Maharashtra"
    },
    {
      icon: "👥",
      title: "Team Size",
      content: "Up to 10 Employees"
    },
  ];

  const values = [
    {
      icon: '🛡️',
      title: 'Quality Assurance',
      text: 'We maintain the highest product standards through rigorous quality control and testing procedures.'
    },
    {
      icon: '🤝',
      title: 'Trust & Transparency', 
      text: 'Building lasting relationships through honest pricing and ethical business practices.'
    },
    {
      icon: '🎯',
      title: 'Customer Focus',
      text: 'Delivering flexible, customized solutions that meet unique client requirements.'
    },
  ];

  return (
    <AboutContainer maxWidth="lg">
      <SEO
        title="About Shree Balaji Corporation"
        description="Learn about Shree Balaji Corporation, a trusted supplier of PET straps, bale covers, leather washers, and strapping machines for the cotton industry since 1970."
        keywords="about, Shree Balaji Corporation, PET straps, bale covers, ginning, Akola, Maharashtra"
        url="/about"
      />
      <Section>
        <Grid container spacing={6} alignItems="center">
          {/* Left Side Content */}
          <Grid item xs={12} md={8}>
            <Typography
              variant="h2"
              sx={{ 
                fontWeight: 700,
                mb: 2,
                color: "primary.main"
              }}
            >
              About Shree Balaji Corporation
            </Typography>
            <Typography
              variant="h5"
              sx={{ 
                color: "text.secondary",
                mb: 4,
                fontWeight: 500
              }}
            >
              Your Trusted Partner in Industrial Packaging Since 1970
            </Typography>

            {/* Mission Statement */}
            <MissionCard>
              <Typography variant="h4" component="h3">
                Our Mission
              </Typography>
              <AboutText>
                To provide high-quality industrial packaging solutions that meet the evolving 
                needs of our clients, ensuring reliability, efficiency, and sustainability in 
                every product we deliver.
              </AboutText>
            </MissionCard>

            <AboutText sx={{ mb: 3 }}>
              Founded in <strong>1970</strong> in <strong>Akola, Maharashtra</strong>,
              <strong> Shree Balaji Corporation</strong> has been a cornerstone in the trading
              and wholesale distribution of industrial packaging materials. Our journey began
              under the visionary leadership of <strong>Late Shri Sureshkumar Shah</strong>,
              whose dedication, ethics, and foresight laid the strong foundation of the company.
            </AboutText>

            <AboutText>
              Today, the legacy is carried forward by his son, <strong>Ashish Shah</strong>,
              who leads the business with the same commitment to quality, integrity, and client
              satisfaction. Under his direction, we continue to expand our reach and strengthen
              our presence across the ginning and pressing industry across India.
            </AboutText>
          </Grid>

          {/* Right Side Images */}
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <AboutImage 
                src="/assets/about-1.jpg" 
                alt="Shree Balaji Corporation"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div style="height: 300px; background: var(--color-secondary); display: flex; align-items: center; justify-content: center; font-size: 4rem; border-radius: 8px;">🏭</div>';
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Section>

      {/* Key Facts Section */}
      <Section>
        <Typography
          variant="h3"
          sx={{
            textAlign: "center",
            mb: 4,
            fontWeight: 600,
            color: "primary.main"
          }}
        >
          Company Facts
        </Typography>
        <ContentCard>
          <Grid container spacing={4}>
            {facts.map((fact, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <div className="value-item">
                  <div className="feature-icon">
                    {fact.icon}
                  </div>
                  <h4>{fact.title}</h4>
                  <p>{fact.content}</p>
                </div>
              </Grid>
            ))}
          </Grid>
        </ContentCard>
      </Section>

      {/* Values Section */}
      <Section>
        <Typography
          variant="h3"
          sx={{
            textAlign: "center",
            mb: 4,
            fontWeight: 600,
            color: "primary.main"
          }}
        >
          Our Core Values
        </Typography>
        <ContentCard>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} md={4} key={index}>
                <div className="value-item">
                  <div className="feature-icon">
                    {value.icon}
                  </div>
                  <h4>{value.title}</h4>
                  <p>{value.text}</p>
                </div>
              </Grid>
            ))}
          </Grid>
        </ContentCard>
      </Section>
    </AboutContainer>
  );
};

export default AboutUs;
